// What a client pipeline shows in this browser: the board or the list, the deal open in its panel, the
// follow-ups or the stages open, and what is being looked for. The view is kept per board and per browser, as
// the camera is on a canvas; the rest is for this visit.

import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import type { ID } from "../../../shared/types";
import { stagesOf, type Deal, type StagesData } from "../../../shared/pipeline";
import { boardEntries, useStore } from "../../state/store";
import { dealsOf } from "../../../shared/pipeline";
import { readStoredJson, writeStoredJson } from "../../lib/stored";

export type PipeView = "board" | "list";
export type PipeSheet = "follow" | "stages" | "new" | "import" | null;

interface PipeUi {
  /** board id to its view */
  view: Record<ID, PipeView>;
  /** the deal open in the panel */
  open: ID | null;
  sheet: PipeSheet;
  /** words a deal's name, contact or next step has, to narrow what shows */
  find: string;
  /** on a phone, the stage shown */
  tab: Record<ID, string>;
  /** the stage a new deal goes in, when it was started from a column */
  newIn: string | null;
}

const KEY = "cb:pipeline";

function load(): Pick<PipeUi, "view"> {
  const d = readStoredJson<Partial<PipeUi>>(KEY, {});
  return { view: d.view && typeof d.view === "object" ? d.view : {} };
}

export const usePipeUi = create<PipeUi>(() => ({ ...load(), open: null, sheet: null, find: "", tab: {}, newIn: null }));

usePipeUi.subscribe((s) => {
  writeStoredJson(KEY, { view: s.view }); // kept for this visit only when storage is out of reach
});

export const setPipe = (patch: Partial<PipeUi>) => usePipeUi.setState(patch);

export function setView(boardId: ID, view: PipeView) {
  usePipeUi.setState((s) => ({ view: { ...s.view, [boardId]: view } }));
}

/** A deal's panel opened (or closed: null). The follow-ups stay behind it, to come back to. */
export const openDeal = (id: ID | null) => usePipeUi.setState((s) => ({ open: id, sheet: id && s.sheet !== "follow" ? null : s.sheet }));

export interface Pipe {
  deals: Deal[];
  stages: StagesData;
  stagesId: ID | null;
}

/** A pipeline's deals and its stages, as the store has them now. */
export function usePipeline(boardId: ID): Pipe {
  const entries = useStore(useShallow((s) => boardEntries(s, boardId).filter((e) => e.t === "deal" || e.t === "stages")));
  const { entry, data } = stagesOf(entries);
  return { deals: dealsOf(entries), stages: data, stagesId: entry?.id ?? null };
}

/** Whether a deal fits the words being looked for. */
export function fits(d: Deal, find: string): boolean {
  const q = find.trim().toLowerCase();
  if (!q) return true;
  return [d.data.company, d.data.contact, d.data.email, d.data.next].some((x) => x.toLowerCase().includes(q));
}
